import type {CmLogger} from "@cm/log"
import type {Catalog, CatalogDiff, CatalogItem, CatalogPrice, GetSubgraphCallback} from "#pricing/catalogs/catalog-interface"
import {ConfigurationList} from "#pricing/nodes/configuration-list"
import {Currency} from "#pricing/types/currency"
import type {PriceMap} from "#pricing/types/pricing-types"
import {PricedItemNode} from "#pricing/nodes/priced-item-node"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"
import type {utils, WorkBook, WorkSheet} from "xlsx"

export class VadoCatalog implements Catalog {
    items: VadoCatalogItem[]

    constructor(items: VadoCatalogItem[]) {
        this.items = items
    }

    search(query: string): CatalogItem[] {
        const lowerCaseQuery = query.toLowerCase()
        return this.items.filter((item) => item.description.toLowerCase().includes(lowerCaseQuery) || item.sku.toLowerCase().includes(lowerCaseQuery))
    }

    getPriceMap(): PriceMap {
        const priceMap: PriceMap = new Map()
        this.items.forEach((item) => priceMap.set(item.sku, new Map([[Currency.Gbp, {price: Number(item.priceIncVat), priceIncludesVat: true}]])))
        return priceMap
    }

    getAllPrices(): CatalogPrice[] {
        const result: CatalogPrice[] = []
        this.items.forEach((item) => result.push({uniqueId: item.sku, price: item.priceIncVat, currency: Currency.Gbp, priceIncludesVat: true})) //TODO: GBP
        return result
    }

    diff(newCatalog: Catalog): CatalogDiff {
        const oldPrices = new Map<string, CatalogPrice>()
        const newPrices = new Map<string, CatalogPrice>()
        this.getAllPrices().forEach((price) => oldPrices.set(price.uniqueId, price))
        newCatalog.getAllPrices().forEach((price) => newPrices.set(price.uniqueId, price))

        const deletedEntries = new Map<string, CatalogPrice>()
        const updatedEntries = new Map<string, CatalogPrice>()
        const newEntries = new Map<string, CatalogPrice>()

        oldPrices.forEach((value, key) => {
            const newPrice = newPrices.get(key)
            if (newPrice === undefined) deletedEntries.set(key, value)
            else if (newPrice.price !== value.price) updatedEntries.set(key, newPrice)
        })

        newPrices.forEach((value, key) => {
            if (!oldPrices.has(key)) newEntries.set(key, value)
        })

        const conflicts = new Map<string, string>()
        for (const deletedEntry of deletedEntries) if (!conflicts.has(deletedEntry[0])) conflicts.set(deletedEntry[0], "Deleted entry")

        return {
            deletedEntries,
            updatedEntries,
            newEntries,
            conflicts,
        }
    }
}

/**
 * Fields every Vado sheet must map. Any further key in a {@link VadoColumnMapping} is free-form and
 * ends up verbatim in {@link VadoCatalogItem.extraFields}, so capturing an additional column (EAN,
 * MOP, EU price, status, ...) is a one-line change to a sheet spec — no parser or class change needed.
 */
export const VADO_REQUIRED_FIELDS = ["sku", "description", "finish", "priceIncVat"] as const

export type VadoRequiredField = (typeof VADO_REQUIRED_FIELDS)[number]

/** Maps an internal field name to the column title as it appears in the sheet's header row. */
export type VadoColumnMapping = Record<string, string> & Record<VadoRequiredField, string>

export type VadoSheetSpec = {
    sheetName: string
    columns: VadoColumnMapping
    /** Mapped fields tolerated as missing from this sheet. The required fields may not be listed here. */
    optionalFields?: string[]
}

/** Sheet specs for the current Vado price list format (2026 Cameo price list). */
export const DEFAULT_VADO_SHEETS: VadoSheetSpec[] = [
    {
        sheetName: "VADO UK",
        columns: {
            sku: "Product Code",
            description: "Description",
            finish: "Finish",
            priceIncVat: "2026 UK RRP Inc. VAT (£)",
            priceExVat: "2026 UK RRP Ex. VAT (£)",
        },
        optionalFields: ["priceExVat"],
    },
    {
        sheetName: "VADO New Categories",
        columns: {
            sku: "Product Code",
            description: "Description",
            finish: "Finish",
            priceIncVat: "2026 UK RRP Inc. VAT (£)",
        },
    },
]

export class VadoCatalogItem implements CatalogItem {
    sku: string
    /** The raw `Description` column — {@link description} is this plus the finish. */
    descriptionCatalog: string
    finish: string
    priceExVat: number | undefined
    priceIncVat: number
    /** Mapped columns beyond the required ones, keyed by internal field name. */
    extraFields: Record<string, unknown>

    constructor(
        sku: string,
        description: string,
        finish: string,
        priceExVat: number | undefined,
        priceIncVat: number,
        extraFields: Record<string, unknown> = {},
    ) {
        this.sku = sku
        this.descriptionCatalog = description
        this.finish = finish
        this.priceExVat = priceExVat
        this.priceIncVat = priceIncVat
        this.extraFields = extraFields
    }

    /** Builds an item from a row already keyed by internal field name (see {@link VadoColumnMapping}). */
    static fromRow(row: Record<string, unknown>): VadoCatalogItem {
        const {sku, description, finish, priceExVat, priceIncVat, ...extraFields} = row
        return new VadoCatalogItem(String(sku).trim(), String(description), String(finish), toOptionalNumber(priceExVat), Number(priceIncVat), extraFields)
    }

    get uniqueId() {
        return this.sku
    }

    get description() {
        return `${this.descriptionCatalog.trim()}, ${this.finish.trim()}`
    }

    createPriceGraph(getSubgraph: GetSubgraphCallback, withCondition = true): PricedItemNode {
        return new PricedItemNode({
            parameters: {
                description: this.description,
                uniqueId: this.uniqueId,
                condition: withCondition
                    ? new VariantConditionNode({
                          parameters: {
                              condition: {variantIds: [], variantOperator: "AND", negated: false},
                              currentConfigurations: new ConfigurationList({parameters: {list: []}}),
                          },
                      })
                    : undefined,
                subPrices: undefined,
                sku: this.sku,
                dependsOnSubprice: false,
                amount: 1,
            },
        })
    }
}

export function parseXlsb(xlsb: Uint8Array, xlsx: typeof import("xlsx"), logger: CmLogger, sheets: VadoSheetSpec[] = DEFAULT_VADO_SHEETS): Catalog {
    const workbook = xlsx.read(xlsb, {type: "buffer", cellDates: true, cellStyles: true, cellNF: true, cellText: true})

    const catalogItems = sheets.flatMap((spec) => parseSheet(workbook, spec, xlsx, logger))

    verifyData(catalogItems) // Across all sheets, so cross-sheet duplicates are caught too

    return new VadoCatalog(catalogItems)
}

function parseSheet(workbook: WorkBook, spec: VadoSheetSpec, xlsx: {utils: typeof utils}, logger: CmLogger): VadoCatalogItem[] {
    const worksheet = workbook.Sheets[spec.sheetName]
    if (!worksheet) throw new Error(`Sheet "${spec.sheetName}" not found. Available sheets: ${workbook.SheetNames.join(", ")}`)

    const optionalFields = new Set(spec.optionalFields ?? [])
    const requiredFields = Object.keys(spec.columns).filter((field) => !optionalFields.has(field))
    for (const field of VADO_REQUIRED_FIELDS) {
        if (optionalFields.has(field)) throw new Error(`Sheet "${spec.sheetName}": field "${field}" is required and cannot be listed as optional`)
    }

    const header = findHeaderRow(
        worksheet,
        requiredFields.map((field) => spec.columns[field]),
        xlsx,
    )
    if (!header) {
        throw new Error(
            `Sheet "${spec.sheetName}": no header row found in the first ${MAX_HEADER_SCAN_ROWS} rows containing all of: ${requiredFields
                .map((field) => spec.columns[field])
                .join(", ")}`,
        )
    }

    // sheet_to_json keys rows by the labels we pass, so resolve each field to a unique label instead of a column index
    const labelByField = new Map<string, string>()
    for (const [field, title] of Object.entries(spec.columns)) {
        const index = header.titles.indexOf(normalizeTitle(title))
        if (index < 0) {
            logger.warn(`Sheet "${spec.sheetName}": optional column "${title}" (field "${field}") not found, skipping it`)
            continue
        }
        labelByField.set(field, header.labels[index])
    }

    const range = xlsx.utils.decode_range(worksheet["!ref"]!)
    range.s.c = 0
    range.s.r = header.rowIndex + 1 // Skip everything up to and including the header row

    const jsonData = xlsx.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
        range: xlsx.utils.encode_range(range),
        header: header.labels,
        blankrows: false,
    })

    const exclude = new Set(["l/min", "for", "and"])

    const rows = jsonData
        .map((jsonRow) => {
            const row: Record<string, unknown> = {}
            for (const [field, label] of labelByField) row[field] = jsonRow[label]
            return row
        })
        // Drops blank rows and the category rows Vado puts between product groups (only the first column is filled)
        .filter((row) => VADO_REQUIRED_FIELDS.every((field) => row[field] != null && row[field] !== ""))

    logger.log(`Sheet "${spec.sheetName}": read ${rows.length} items, skipped ${jsonData.length - rows.length} rows`)

    return rows.map((row) =>
        VadoCatalogItem.fromRow({
            ...row,
            description: capitalize(String(row["description"]), exclude),
            finish: capitalize(String(row["finish"]), exclude),
        }),
    )
}

const MAX_HEADER_SCAN_ROWS = 20

/**
 * Vado puts a title/date block above the header, and the header row index differs per sheet, so find
 * it by looking for the row that carries all required column titles.
 */
function findHeaderRow(
    worksheet: WorkSheet,
    requiredTitles: string[],
    xlsx: {utils: typeof utils},
): {rowIndex: number; titles: string[]; labels: string[]} | undefined {
    const range = xlsx.utils.decode_range(worksheet["!ref"]!)
    const normalizedRequired = requiredTitles.map(normalizeTitle)
    const lastRow = Math.min(range.e.r, range.s.r + MAX_HEADER_SCAN_ROWS)

    for (let r = range.s.r; r <= lastRow; r++) {
        const titles: string[] = []
        for (let c = 0; c <= range.e.c; c++) titles.push(normalizeTitle(worksheet[xlsx.utils.encode_cell({r, c})]?.v))
        if (!normalizedRequired.every((title) => titles.includes(title))) continue

        // Empty and duplicate titles would collide as sheet_to_json keys
        const used = new Set<string>()
        const labels = titles.map((title, index) => {
            if (title === "" || used.has(title)) return `__column_${index}`
            used.add(title)
            return title
        })
        return {rowIndex: r, titles, labels}
    }

    return undefined
}

function normalizeTitle(value: unknown): string {
    return String(value ?? "")
        .replace(/\s+/g, " ")
        .trim()
}

function toOptionalNumber(value: unknown): number | undefined {
    if (value == null || value === "") return undefined
    return Number(value)
}

function capitalize(sentence: string, exclude: Set<string>): string {
    return sentence
        .split(" ")
        .map((word) => {
            if (exclude.has(word)) return word
            return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join(" ")
}

function verifyData(catalogItems: VadoCatalogItem[]) {
    const uniqueIds = new Set<string>()
    catalogItems.forEach((item) => {
        if (uniqueIds.has(item.sku)) throw new Error(`Duplicate SKU: ${item.sku}`)
        uniqueIds.add(item.sku)
    })
}
